/**
 * What the port's own dialogs over a popup of the game share (the account dialog of the settings screen, the
 * delete-account dialog: src/game/ui/account-dialog.ts, delete-account.ts):
 *  - on a wide screen (a phone, a tablet: Stage.visibleRect) the dialog's frame and content are shown larger, as the
 *    game's popups are (src/game/ui/wide-popups.ts fitArea): the whole frame within the shown safe area, its text
 *    about BODY_CSS, at one scale whether a field of it has the focus or not (a first tap that only takes the focus
 *    away moved nothing: the button under the finger is the one it lands on). Only while the host shows a focused field
 *    above an on-screen keyboard (Stage.$keyboardView: its view zoomed to the field) the scale stays as it was;
 *  - the popup under the dialog (the settings screen) is hidden while it is open: pieces of it round a dialog shown
 *    larger looked like a screen taken apart; it is shown again when the dialog closes.
 */
import type { Area } from './wide-popups';
import { fitArea } from './wide-popups';

/**
 * Scales and places `body` (the dialog's frame and content, drawn in stage coordinates at scale 1 with the frame's
 * rectangle `frame`) to fit the shown safe area of `stage`; back to scale 1 without a wide host. While the host shows a
 * field above an on-screen keyboard the scale stays as it is. Returns the scale.
 */
export function fitDialogBody(body: any, frame: Area, stage: any): number {
  if (!stage || stage.$keyboardView) return body.scaleX;
  const f = stage.$visibleArea && stage.cssScale > 0 ? fitArea(frame, rectOf(stage.safeRect), stage.cssScale) : { k: 1, x: 0, y: 0 };
  if (body.scaleX !== f.k) body.scaleX = f.k;
  if (body.scaleY !== f.k) body.scaleY = f.k;
  if (body.x !== f.x) body.x = f.x;
  if (body.y !== f.y) body.y = f.y;
  return f.k;
}

function rectOf(r: any): Area {
  return { x: r.x, y: r.y, width: r.width, height: r.height };
}

/** Hides the screen on top of the game's stack (the popup a dialog opens over); returns what shows it again. */
export function hideScreenUnder(fwp: any): () => void {
  const i = fwp && fwp._interf;
  const top = i && i.cur_screen ? i.cur_screen[i.cur_screen[0]] : null;
  const link = top && top.link;
  if (!link || !link.visible) return () => undefined;
  link.visible = false;
  return () => {
    link.visible = true;
  };
}
